<script setup lang="ts">
import BaseModal from './BaseModal.vue'
import type { EventStatus } from '@/types/event'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    detailType: 'football' | 'basketball' | null
    status?: EventStatus
  }>(),
  { status: 'open' },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  register: []
  contact: []
}>()

const footballData = {
  badge: 'Football',
  title: 'M6 Elite Summer Combine',
  intro:
    'June 27, 2026 at King Louies Outdoor Fields, Louisville, KY. Ages 14+ | Co-ed | 100 spots available | 10 a.m.–1 p.m.',
  tested: [
    'Height & Weight',
    'Pro-Agility (5-10-5)',
    'Vertical Jump',
    'Broad Jump',
    '40-Yard Dash',
  ],
  knowItems: [
    'Wear football cleats and appropriate athletic clothing',
    'Open to both male and female athletes',
    'Arrive 30 minutes early for combine check-in at the registration station',
    'Screening add-on? Arrive 1 hour early (15 min. before your screening begins)',
    'Hydration station provided — bring your own water bottle as well',
  ],
  promoNote: null,
  price: '$125',
}

const basketballData = {
  badge: 'Basketball',
  title: 'M6 Elite Combine x Shoot 360',
  intro:
    'July 25 (Ages 10–13) & August 1 (Ages 14+) at Shoot 360, 9400 Dayflower St, Prospect, KY 40059. Co-ed | 50 spots per session | 10 a.m.–1 p.m.',
  tested: [
    'Height, Weight, Standing Reach & Wingspan',
    'Max Vertical Jump',
    'VALD Force Deck Countermovement Jump',
    'Lane Agility Drill',
    '3/4 Court Sprint',
  ],
  knowItems: [
    'Wear basketball shoes and appropriate athletic clothing',
    'Open to both male and female athletes',
    'Arrive 30 minutes early for combine check-in at the registration station',
    'Screening add-on? Arrive 1 hour early (15 min. before your screening begins)',
    'Hydration station provided — bring your own water bottle as well',
  ],
  promoNote: '✨ First 20 signups: use code SHOOT25 for $25 off!',
  price: '$125',
}

const addons = [
  {
    title: 'Professional Video Footage',
    price: '+$25',
    description:
      'HD footage of 1–2 of your combine drills. Custom feedback on movement efficiency available upon request.',
  },
  {
    title: 'Comprehensive Athlete Screening by Aptiva Health',
    price: '+$50',
    description:
      'Hands-on evaluation by a Certified Athletic Trainer or Physical Therapist covering mobility, balance, range of motion, functional movement, and grip strength — designed to identify injury risk and areas for improvement.',
  },
]

function close() {
  emit('update:modelValue', false)
}
</script>

<template>
  <BaseModal :model-value="modelValue" max-width="620px" @update:model-value="close">
    <template v-if="detailType">
      <div class="lm-body">
        <div
          v-for="data in [detailType === 'football' ? footballData : basketballData]"
          :key="detailType"
        >
          <div
            v-if="status !== 'open'"
            class="lm-status-banner"
            :class="status === 'cancelled' ? 'lm-status-banner--cancelled' : 'lm-status-banner--closed'"
          >
            {{ status === 'cancelled' ? 'This event has been cancelled.' : 'Registration for this event is closed.' }}
          </div>
          <div class="lm-badge">{{ data.badge }}</div>
          <h2>{{ data.title }}</h2>
          <p>{{ data.intro }}</p>

          <div class="lm-badge section-badge">What You'll Be Tested On</div>
          <ul class="lm-features">
            <li v-for="item in data.tested" :key="item">
              <span class="lm-check">&#10003;</span>{{ item }}
            </li>
          </ul>

          <div class="lm-badge section-badge">What to Know</div>
          <ul class="lm-features">
            <li v-for="item in data.knowItems" :key="item">
              <span class="lm-check">&#10003;</span>{{ item }}
            </li>
          </ul>

          <div class="lm-badge section-badge">Add-On Services</div>
          <ul class="lm-features addon-list">
            <li v-for="addon in addons" :key="addon.title">
              <span class="lm-check">&#10003;</span>
              <span>
                {{ addon.title }} <strong class="addon-price">{{ addon.price }}</strong
                ><br />
                <span class="addon-desc">{{ addon.description }}</span>
              </span>
            </li>
          </ul>

          <p v-if="data.promoNote" class="promo-note">{{ data.promoNote }}</p>

          <div v-if="status === 'open'" class="lm-cta-row">
            <button class="btn-primary" @click="emit('register')">
              Register Now — {{ data.price }}
            </button>
            <button class="btn-outline" @click="emit('contact')">Contact Us</button>
          </div>
          <div v-else class="lm-cta-row lm-cta-row--status">
            <button class="btn-outline" @click="emit('contact')">Contact Us</button>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<style scoped>
.lm-body {
  padding: 32px;
}

.lm-body h2 {
  font-family: 'HND', sans-serif;
  font-weight: 950;
  font-size: 1.6rem;
  color: var(--white);
  text-transform: uppercase;
  margin-bottom: 6px;
}

.lm-badge {
  display: inline-block;
  font-family: 'HND', sans-serif;
  font-size: 0.68rem;
  color: var(--pink-bright);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.section-badge {
  display: block;
  margin-top: 20px;
  margin-bottom: 4px;
}

.lm-body > div > p {
  font-family: 'HNT', sans-serif;
  font-size: 0.92rem;
  color: var(--text);
  line-height: 1.75;
  margin-bottom: 16px;
}

.lm-features {
  list-style: none;
  padding: 0;
  margin: 0 0 8px;
}

.lm-features li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-family: 'HNT', sans-serif;
  font-size: 0.88rem;
  color: var(--text);
  line-height: 1.6;
}

.lm-features li:last-child {
  border-bottom: none;
}

.lm-check {
  color: var(--pink-bright);
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.addon-price {
  color: var(--pink-bright);
  font-family: 'HND', sans-serif;
  font-size: 0.85rem;
}

.addon-desc {
  font-size: 0.82rem;
  font-weight: 400;
  color: var(--text);
}

.promo-note {
  font-style: italic;
  color: var(--pink-bright) !important;
  font-size: 0.88rem !important;
  margin-top: 12px;
}

.lm-status-banner {
  font-family: 'HND', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #fff;
}

.lm-status-banner--cancelled {
  background: #e53935;
}

.lm-status-banner--closed {
  background: #757575;
}

.lm-cta-row--status {
  justify-content: center;
}

.lm-cta-row {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary,
.btn-outline {
  flex: 1;
  padding: 13px 20px;
  border-radius: 9999px;
  cursor: pointer;
  font-family: 'HND', sans-serif;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.btn-primary {
  background: linear-gradient(135deg, var(--pink), var(--purple));
  color: #fff;
  border: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(186, 50, 139, 0.4);
}

.btn-outline {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.25);
  color: #fff;
}

.btn-outline:hover {
  border-color: var(--pink-bright);
  color: var(--pink-bright);
}

@media (max-width: 768px) {
  .lm-body {
    padding: 20px;
  }

  .lm-body h2 {
    font-size: 1.3rem;
  }

  .lm-cta-row {
    flex-direction: column;
  }
}
</style>
